import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { authorizedAxiosInstance } from '@/clients';

const exchangeVariantSchema = z
    .object({
        exchange: z.string().nullish(),
        exchangeShortName: z.string().nullish(),
        currency: z.string().nullish(),
        name: z.string().nullish(),
        price: z.coerce.number().finite().nullish(),
        stockExchange: z.string().nullish(),
        symbol: z.string().trim().min(1),
        timestamp: z.coerce.number().int().nonnegative().nullish(),
        type: z.string().nullish(),
    })
    .passthrough();

const exchangeVariantsSchema = z.array(exchangeVariantSchema);
const exchangeVariantQuotesSchema = z.array(
    exchangeVariantSchema.pick({
        currency: true,
        exchange: true,
        name: true,
        price: true,
        symbol: true,
        timestamp: true,
    }),
);
const LIVE_PRICE_REFRESH_INTERVAL = 15 * 1000;

export interface FmpExchangeVariant {
    currency?: string | null;
    exchange?: string | null;
    exchangeShortName?: string | null;
    name?: string | null;
    price?: number | null;
    stockExchange?: string | null;
    symbol: string;
    timestamp?: number | null;
    type?: string | null;
}

const normalizeSymbol = (symbol: string) => symbol.trim().toUpperCase();

const fetchExchangeVariants = async (symbol: string, signal: AbortSignal): Promise<FmpExchangeVariant[]> => {
    try {
        const response = await authorizedAxiosInstance.get<unknown>('/api/vendors/fmp/search/exchange-variants', {
            params: { query: normalizeSymbol(symbol) },
            signal,
        });
        const variants = exchangeVariantsSchema.parse(response.data);
        const symbols = Array.from(new Set(variants.map((variant) => normalizeSymbol(variant.symbol))));
        let quotesBySymbol = new Map<string, FmpExchangeVariant>();

        if (symbols.length) {
            try {
                const quoteResponse = await authorizedAxiosInstance.get<unknown>('/api/vendors/fmp/quotes/batch', {
                    params: { symbols: symbols.join(',') },
                    signal,
                });
                const quotes = exchangeVariantQuotesSchema.parse(quoteResponse.data);

                quotesBySymbol = new Map(quotes.map((quote) => [normalizeSymbol(quote.symbol), quote]));
            } catch (error) {
                if (!signal.aborted) {
                    captureException(error, { tags: { query: 'exchange-variant-quotes' } });
                }
            }
        }

        return Array.from(
            new Map(
                variants.map((variant) => {
                    const quote = quotesBySymbol.get(normalizeSymbol(variant.symbol));

                    return [
                        `${variant.exchangeShortName ?? variant.exchange ?? variant.stockExchange ?? ''}:${variant.symbol}`,
                        {
                            ...variant,
                            currency: quote?.currency ?? variant.currency,
                            exchange: variant.exchange ?? quote?.exchange,
                            name: variant.name ?? quote?.name,
                            price: quote?.price ?? variant.price,
                            timestamp: quote?.timestamp ?? variant.timestamp,
                        },
                    ];
                }),
            ).values(),
        );
    } catch (error) {
        if (!signal.aborted) {
            captureException(error, { tags: { query: 'exchange-variants' } });
        }

        throw error;
    }
};

export const exchangeVariantQueryKeys = {
    bySymbol: (symbol: string) => ['vendors', 'fmp', 'exchange-variants', normalizeSymbol(symbol)] as const,
};

export const exchangeVariantQueryOptions = {
    bySymbol: (symbol: string) =>
        queryOptions({
            enabled: Boolean(symbol.trim()),
            queryFn: ({ signal }) => fetchExchangeVariants(symbol, signal),
            queryKey: exchangeVariantQueryKeys.bySymbol(symbol),
            refetchInterval: LIVE_PRICE_REFRESH_INTERVAL,
            staleTime: LIVE_PRICE_REFRESH_INTERVAL,
        }),
};
